import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Swarm } from '@lbs/swarm-client';

import Config from '@skin_config';
import Helpers from '@common/helpers/helperFunctions';
import PaymentsService, { SessionBalanceStates } from '@app/dev/services/payments';
import { asyncWrapper } from '@common/hocs/asyncWrapper';
import { getBalanceSubscribtionRequest } from '@app/dev/helpers/terminalRequests/balance';
import { TerminalDepositModesEnum } from '@app/dev/constants/enums/terminalDepositModes';
import { isMultiDepositModeEnabled } from '@app/dev/helpers/multiDepositMode';
import { usePrevious } from '@common/hooks/previous';
import { setBalanceToTerminal } from '@app/dev/helpers/balance';

import Money from '../money';
import { useBalance } from '../../hooks/balance';
import {
  SetTerminalBalance,
  setLastMoneyInputType,
  setTerminalDepositMode,
  SetTerminalSuperbetData,
  savePlayerPaymentInfo,
} from '../../../actions/terminal';

import './style.scss';
import { todoImplementErrorHandling } from '@common/helpers';
import { GetProfile } from '@app/dev/helpers/selectors';

const getCurrentTerminal = (data) => Helpers.firstElement(data?.terminals);

const Balance = () => {
  const dispatch = useDispatch();
  const profile = useSelector(GetProfile);
  const finalBalance = useBalance(false);
  const [isBalanceVisible, setIsBalanceVisible] = useState(true);
  const { authData, depositMode, savedPlayerInfo } = useSelector((state) => state.terminal);
  const [balance, setBalance] = useState();
  const previousBalance = usePrevious(balance);
  const { tid, enableOnlinePayment } = authData;

  const updateTerminalDepositMode = useCallback(async () => {
    if ((previousBalance === balance && previousBalance !== undefined) || Number.isNaN(balance)) {
      return;
    }

    let updatedDepositMode;
    const emptySessionState = PaymentsService.getSessionBalanceEmptyState();

    const shouldSwitchToCashMode = balance >= Config.main.amountToSwitchToCashMode;

    if (!shouldSwitchToCashMode && savedPlayerInfo?.PersonalId && balance !== 0) {
      await PaymentsService.setSessionBalanceState({
        ...emptySessionState,
        ...savedPlayerInfo,
        SessionBalanceState: SessionBalanceStates.Neutral,
      });

      updatedDepositMode = TerminalDepositModesEnum.Ticket;
      await PaymentsService.setPlayerInfo(savedPlayerInfo);
      dispatch(savePlayerPaymentInfo(emptySessionState));
    } else if (balance === 0) {
      PaymentsService.clearSessionBalanceState();
      dispatch(savePlayerPaymentInfo(emptySessionState));
      updatedDepositMode = TerminalDepositModesEnum.Ticket; // old cash
      // need to clear saved player info, maybe save with null values
    } else if (depositMode === TerminalDepositModesEnum.Ticket) {
      const { state, playerInfo } = await PaymentsService.getSessionBalanceState();

      updatedDepositMode = state;
      if (state === TerminalDepositModesEnum.Cash) {
        if (playerInfo?.PersonalId) {
          await PaymentsService.setSessionBalanceState({
            ...emptySessionState,
            SessionBalanceState: SessionBalanceStates.Cash,
            Id: playerInfo.Id,
          });
        }
      } else if (playerInfo && playerInfo?.PersonalId) {
        await PaymentsService.setPlayerInfo(playerInfo);
      }
    }

    if (updatedDepositMode) {
      dispatch(setLastMoneyInputType(updatedDepositMode));
      dispatch(setTerminalDepositMode(updatedDepositMode));
    }
  }, [balance, previousBalance, dispatch, depositMode]);

  useEffect(() => {
    let subId;
    const swarmInstance = Swarm.getInstance();

    (async () => {
      try {
        const { data, subid } = await swarmInstance.subscribe(
          getBalanceSubscribtionRequest(tid),
          (balanceData) => {
            const currentTerminal = getCurrentTerminal(balanceData);

            setBalance(currentTerminal?.balance);
            dispatch(SetTerminalSuperbetData(currentTerminal?.super_bet));
          },
          true
        );

        setBalance(getCurrentTerminal(data)?.balance);

        subId = subid;
      } catch {
        todoImplementErrorHandling();
      }
    })();

    return () => subId && swarmInstance.unsubscribe(subId);
  }, [dispatch, tid]);

  useEffect(() => {
    if ((balance || balance === 0) && previousBalance !== balance) {
      dispatch(
        SetTerminalBalance({
          session_balance: balance,
        }),
      );
      setBalanceToTerminal(balance);
    }

    if (isMultiDepositModeEnabled()) updateTerminalDepositMode();
  }, [previousBalance, balance, dispatch, updateTerminalDepositMode, enableOnlinePayment]);

  const toggleBalanceVisible = () => setIsBalanceVisible(!isBalanceVisible);

  return (
    <div className="single-nav-item">
      {(!Config.header.disableBalance && (!Config.header.disableAnonymousBalance || !!profile)) && (
        <p className={`balance-view-terminal ${isBalanceVisible ? 'balance-visible' : ''}`}>
          <i
            onClick={toggleBalanceVisible}
            className={`balance-view-icon ${isBalanceVisible ? 'icon-eye-hide active-enabled' : 'icon-eye-show active'}`}
          />
          <span>
            {!finalBalance && finalBalance !== 0 ? '-' : <Money amount={finalBalance} />}
          </span>
        </p>
      )}
    </div>
  );
};

export default asyncWrapper(Balance);
